/**
 * NotificationToaster — listens for the `cs-notification` window event and:
 *   - Shows a sonner toast for `priority: 'high'` events
 *   - Plays a soft chime (if user enabled sound)
 *   - Fires confetti for `category: 'bingo'` wins
 *
 * Lives near the root once.  Renders nothing visible itself; sonner's
 * `<Toaster />` (mounted elsewhere) handles the actual UI.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const SOUND_KEY = 'cs-notif-sound-v1';

// Tiny 200ms synth chime — no asset to ship, generated via WebAudio.
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine';
    o.frequency.setValueAtTime(660, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
    o.start();
    o.stop(ctx.currentTime + 0.34);
    setTimeout(() => ctx.close().catch(() => {}), 500);
  } catch (_) { /* noop */ }
}

async function fireConfetti() {
  try {
    const { default: confetti } = await import('canvas-confetti');
    confetti({
      particleCount: 80,
      spread: 70,
      origin: { x: 0.5, y: 0.4 },
      colors: ['#CD040B', '#F59E0B', '#10B981', '#A855F7', '#EC4899'],
      disableForReducedMotion: true,
    });
  } catch (_) { /* noop */ }
}

const NotificationToaster = () => {
  const navigate = useNavigate();
  const lastFiredRef = useRef(0);

  useEffect(() => {
    const handler = (e) => {
      const n = e.detail || {};
      const soundOn = localStorage.getItem(SOUND_KEY) === '1';
      // Debounce burst arrivals (≤500ms) so a rapid bingo barrage doesn't
      // spawn 10 toasts on top of each other.
      const now = Date.now();
      const debounced = now - lastFiredRef.current < 500;
      lastFiredRef.current = now;

      if (n.priority === 'high' && !debounced) {
        toast(n.title || 'Notification', {
          description: n.body || undefined,
          duration: 7000,
          action: n.url && n.url !== '/' ? {
            label: 'Go',
            onClick: () => {
              try {
                const u = new URL(n.url, window.location.origin);
                navigate(u.pathname + u.search + u.hash);
              } catch { navigate(n.url); }
            },
          } : undefined,
        });
        if (soundOn) playChime();
      }
      if (n.category === 'bingo') {
        fireConfetti();
      }
    };
    window.addEventListener('cs-notification', handler);
    return () => window.removeEventListener('cs-notification', handler);
  }, [navigate]);

  return null;
};

export default NotificationToaster;
